import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { getAuthToken } from '../utils/auth';

const TourOperatorManagement = () => {
  const [operators, setOperators] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    commissionRate: '',
    rating: 5,
    specializations: '',
    isActive: true
  });

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get('/master-data/tour-operators', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperators(response.data.operators || response.data);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      const dataToSend = {
        ...formData,
        specializations: formData.specializations.split(',').map(s => s.trim())
      };

      if (editingId) {
        await axios.put(`/master-data/tour-operators/${editingId}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/master-data/tour-operators', dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      fetchOperators();
      resetForm();
    } catch (error) {
      console.error('Error saving operator:', error);
      alert('Error saving tour operator');
    }
  };

  const handleEdit = (operator) => {
    setFormData({
      ...operator,
      specializations: operator.specializations.join(', ')
    });
    setEditingId(operator._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tour operator?')) {
      try {
        const token = getAuthToken();
        await axios.delete(`/master-data/tour-operators/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchOperators();
      } catch (error) {
        console.error('Error deleting operator:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      commissionRate: '',
      rating: 5,
      specializations: '',
      isActive: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Tour Operators</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ Add Tour Operator'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit Tour Operator' : 'Add New Tour Operator'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Company Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Person *</label>
              <input
                type="text"
                required
                value={formData.contactPerson}
                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows="2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Commission Rate (%) *</label>
              <input
                type="number"
                required
                step="0.01"
                value={formData.commissionRate}
                onChange={(e) => setFormData({ ...formData, commissionRate: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Rating (1-5)</label>
              <input
                type="number"
                min="1"
                max="5"
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Specializations (comma-separated)</label>
              <input
                type="text"
                value={formData.specializations}
                onChange={(e) => setFormData({ ...formData, specializations: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="International Tours, Honeymoon Packages, Adventure Tours"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 mt-6">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">Active</span>
              </label>
            </div>
            <div className="col-span-2 flex gap-2">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                {editingId ? 'Update' : 'Save'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {operators.map((operator) => (
          <div key={operator._id} className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-lg">{operator.name}</h3>
                <p className="text-gray-600 text-sm">{operator.contactPerson}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-yellow-500">★</span>
                <span className="font-semibold">{operator.rating}</span>
              </div>
            </div>
            <div className="space-y-2 text-sm mb-3">
              <p className="text-gray-700">📧 {operator.email}</p>
              <p className="text-gray-700">📞 {operator.phone}</p>
              <p className="text-gray-700">💰 Commission: {operator.commissionRate}%</p>
            </div>
            {operator.specializations.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {operator.specializations.map((spec, idx) => (
                  <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                    {spec}
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(operator)}
                className="flex-1 bg-blue-50 text-blue-600 px-3 py-1 rounded text-sm hover:bg-blue-100"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(operator._id)}
                className="flex-1 bg-red-50 text-red-600 px-3 py-1 rounded text-sm hover:bg-red-100"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TourOperatorManagement;
